import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import { UserXPBadge } from '../gamification';
import { useScreenType } from '../../hooks/useScreenType';
import { partnerService } from '../../services/partnerService';
import { adminMessagesService } from '../../services/adminMessagesService';

interface UserData {
  email: string;
  full_name: string;
  role?: {
    name: string;
  };
  social_profile?: {
    pseudonym?: string;
  };
}

const HeaderRight: React.FC = () => {
  const location = useLocation();
  const { token, logout, hasPermission } = useAuth();
  const { status: gamificationStatus } = useGamificationContext();
  const { isMobile } = useScreenType();
  const [user, setUser] = useState<UserData | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [unreadAdminCount, setUnreadAdminCount] = useState<number>(0);
  const [isNotifOpen, setIsNotifOpen] = useState<boolean>(false);
  const [isPartner, setIsPartner] = useState<boolean>(false);
  const notifRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const checkPartnerStatus = async () => {
      if (token) {
        try {
          const stats = await partnerService.getStats();
          setIsPartner(stats.is_partner);
        } catch (error) {
          setIsPartner(false);
        }
      } else {
        setIsPartner(false);
      }
    };
    checkPartnerStatus();
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/v1/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  const fetchUnreadAdminCount = useCallback(async () => {
    try {
      if (!token) return;
      const nonLus = await adminMessagesService.getUnreadCount(token);
      if (nonLus !== null) setUnreadAdminCount(nonLus);
    } catch (error) {
      console.error('Error fetching admin unread count:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
      fetchUnreadCount();
    }
  }, [token, fetchUser, fetchUnreadCount]);

  useEffect(() => {
    // Only fetch admin unread count if user is loaded and has permission
    // This avoids the race condition 401 when the token is fresh but state not yet synced
    if (token && user && (user.role?.name === 'global_admin' || hasPermission('admin_platform'))) {
      fetchUnreadAdminCount();
    }
  }, [token, user, hasPermission, fetchUnreadAdminCount]);


  return (
    <div className='header-right no-select'>
      <div className='flex flex-center'>
        <ul className='header-menu nowrap'>
          {/* <li>
            <Link
              to='/market'
              className={location.pathname.toLowerCase().includes('/market') ? 'active' : 'passive'}
            >
              Market
            </Link>
          </li> */}
          {/* Nia — visible uniquement pour les utilisateurs Pro */}
          {user?.role?.name && ['global_admin', 'admin_platform', 'paid_user', 'paid_user_pro', 'paid_user_experimental'].includes(user.role.name) && (
            <li>
              <Link
                to='/chat'
                className={location.pathname.toLowerCase().includes('/chat') ? 'active' : 'passive'}
              >
                Nia ✨
              </Link>
            </li>
          )}
          <li>
            <Link
              to='/market/briefing'
              className={location.pathname.toLowerCase().includes('/market/briefing') ? 'active' : 'passive'}
            >
              Flash Marché
            </Link>
          </li>
          <li>
            <Link
              to='/data'
              className={location.pathname.toLowerCase().includes('/data') ? 'active' : 'passive'}
            >
              Terminal
            </Link>
          </li>
          {/* Docs Link Hidden */}
          {/* <li>
            <Link
              to='/docs'
              className={location.pathname.toLowerCase().includes('/docs') ? 'active' : 'passive'}
            >
              Docs
            </Link>
          </li> */}
          {/* API Link Hidden */}
          {/* <li>
            <Link
              to='/api'
              className={location.pathname.toLowerCase().includes('/api') ? 'active' : 'passive'}
            >
              API
            </Link>
          </li> */}
        </ul>
        <ul className='header-icons nowrap'>
          {/* Gamification XP Badge — hidden on mobile */}
          {!isMobile && gamificationStatus && (
            <li style={{ marginRight: '8px' }}>
              <UserXPBadge
                totalXp={gamificationStatus.total_xp}
                level={gamificationStatus.level}
              />
            </li>
          )}
          {isPartner && (
            <li>
              <Link to='/settings/partner' title='Espace Partenaire' aria-label='Espace Partenaire'>
                <i className='material-icons' style={{ color: '#f59e0b' }}>handshake</i>
              </Link>
            </li>
          )}
          {hasPermission('admin_platform') ? (
            <li style={{ position: 'relative' }} ref={notifRef}>
              <button
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                title='Centre de notifications'
                style={{ background: 'none', border: 'none', cursor: 'pointer', outline: 'none', marginTop: '4px', position: 'relative' }}
              >
                {(unreadCount + unreadAdminCount) > 0 && (
                  <span className='notification-badge' style={{
                    position: 'absolute', top: '-10px', right: '-12px'
                  }}>
                    {(unreadCount + unreadAdminCount) > 99 ? '99+' : (unreadCount + unreadAdminCount)}
                  </span>
                )}
                <i className='material-icons' style={{ color: 'var(--text-heading)' }}>notifications</i>
              </button>

              {isNotifOpen && (
                <div style={{
                  position: 'absolute', top: '45px', right: '-10px', width: '300px', background: 'white',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)', borderRadius: '12px', zIndex: 1000, overflow: 'hidden', border: '1px solid #e2e8f0'
                }}>
                  <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#1e293b' }}>
                    Centre de Notifications
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Link 
                      to='/settings/messages' 
                      onClick={() => setIsNotifOpen(false)}
                      style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '14px', textDecoration: 'none', color: '#334155' }}
                      className='notif-item-hover'
                    >
                      <div style={{ background: '#e0e7ff', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className='material-icons' style={{ color: '#4f46e5', fontSize: '20px' }}>notifications_active</i>
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Alertes Système</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          {unreadCount > 0 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{unreadCount} non lu(s)</span> : 'Gérer vos alertes'}
                        </div>
                      </div>
                    </Link>

                    <Link 
                      to='/admin/messages' 
                      onClick={() => setIsNotifOpen(false)}
                      style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', textDecoration: 'none', color: '#334155' }}
                      className='notif-item-hover'
                    >
                      <div style={{ background: '#fce7f3', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className='material-icons' style={{ color: '#db2777', fontSize: '20px' }}>mail</i>
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Messages Utilisateurs</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          {unreadAdminCount > 0 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{unreadAdminCount} nouveau(x)</span> : "Gérer les tickets"}
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </li>
          ) : (
            <li>
              <Link to='/settings/messages' title='Mes notifications'>
                {unreadCount > 0 && (
                  <span className='notification-badge'>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
                <i className='material-icons'>notifications</i>
              </Link>
            </li>
          )}
        </ul>
        <ul className='header-user nowrap'>
          <li>
            <Link to='/members' className='profile-link'>
              <span className='user-info-text'>
                <span className='user-name'>
                  {user?.full_name || 'Utilisateur'}
                  {user?.role?.name && ['paid_user', 'paid_user_pro', 'paid_user_experimental'].includes(user.role.name) && (
                    <span className='pro-badge'>PRO</span>
                  )}
                </span>
                <span className='user-email'>@{user?.social_profile?.pseudonym || user?.email?.split('@')[0] || 'user'}</span>
              </span>
              <i className="material-icons">account_circle</i>
            </Link>
          </li>
          <li className='responsive-hide'>
            <button
              onClick={logout}
              className='signout'
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}
            >
              <i className='material-icons'>power_settings_new</i>
            </button>
          </li>
        </ul>
      </div >
    </div >
  );
};

export default HeaderRight;
